import { allTokenDenom } from '@/services/token-service'
import { useEffect } from 'react'
import { create } from 'zustand'
import { useSession } from './SessionProvider'
import { createComputed } from 'zustand-computed'
import { delay } from '@/helpers/delay'
import { USDC_NEW_DENOM, SHU_DENOM, USDC_DENOM } from '@/constants/constants'
import { LocalStorageKey } from '@/constants/localstorage'
import { TokenMoney } from '@ninjablaze/sdk-ts'

export type UserAccount = Record<
  string,
  {
    height: number
    gameAccountUpdated: number
    amount: TokenMoney
    bank: TokenMoney
    bonus: TokenMoney

    game_balance: TokenMoney
    game_main_balance: TokenMoney
    game_bonus_balance: TokenMoney
    game_main_contract_balance: TokenMoney
    game_bonus_contract_balance: TokenMoney

    full_balance: TokenMoney
  }
>
export type ViewUserAccount = Record<string, TokenMoney>

export const initialUserBalance = Object.fromEntries(
  allTokenDenom.filter((denom) => denom !== '').map((denom) => [
    denom,
    {
      height: 0,
      gameAccountUpdated: 0,
      amount: new TokenMoney(0, denom),
      bank: new TokenMoney(0, denom),
      bonus: new TokenMoney(0, denom),
      game_balance: new TokenMoney(0, denom),
      game_main_balance: new TokenMoney(0, denom),
      game_bonus_balance: new TokenMoney(0, denom),
      game_main_contract_balance: new TokenMoney(0, denom),
      game_bonus_contract_balance: new TokenMoney(0, denom),
      full_balance: new TokenMoney(0, denom),
    },
  ])
)

interface BalanceStateComputed {
  viewBalance: ViewUserAccount

  warningBalance: boolean
}

const computed = createComputed<BalanceState, BalanceStateComputed>((state) => {
  if (!state.freezedBalance) {
    return {
      viewBalance: Object.fromEntries(
        Object.entries(state.balance).map(([denom, value]) => [denom, value.full_balance])
      ),
      warningBalance: false,
    }
  }

  const warningBalance = Object.entries(state.freezedBalance).some(([denom, amount]) => {
    return amount > state.balance[denom]
  })

  // const viewBalance = Object.entries(state.balance).reduce<ViewUserAccount>((acc, [denom, token]) => {
  //   acc[denom] = new TokenMoneyWithHeight(
  //     BigNumber.min(token.chainValue, state.freezedBalance![denom].chainValue),
  //     denom,
  //     token.height
  //   )
  //   return acc
  // }, {})

  return {
    viewBalance: Object.fromEntries(
      Object.entries(state.freezedBalance).map(([denom, value]) => [denom, value.full_balance])
    ),
    warningBalance: warningBalance,
  }
})

export interface BalanceState {
  $init: () => void
  isLoading: boolean
  denom: string
  raw: { [denom: string]: { amount: number } }
  balance: UserAccount

  freezedBalanceSeq: number
  freezedBalanceStatus: boolean
  freezedBalance: UserAccount | undefined
  freezedBalanceHeight: number | undefined

  lockBalance: (height?: number) => void
  unLockBalance: (force?: boolean) => void
  updateBalance: (balances: UserAccount, partial?: boolean) => void
  clearBalance: () => void
}

export const useBalanceStore = create<BalanceState>()(
  computed((set, get) => ({
    denom: USDC_NEW_DENOM,
    isLoading: true,
    balance: initialUserBalance,
    raw: {},

    freezedBalanceStatus: false,
    freezedBalance: undefined,
    freezedBalanceSeq: 0,
    freezedBalanceHeight: undefined,

    lockBalance: (height?: number) => {
      set((state) => ({
        freezedBalanceSeq: state.freezedBalanceSeq + 1,
        freezedBalance: get().isLoading ? undefined : state.balance,
        freezedBalanceStatus: true,
        freezedBalanceHeight: height,
      }))
    },
    unLockBalance: (force: boolean = false) => {
      const { freezedBalanceSeq } = get()

      delay(force ? 0 : 1600).then(() => {
        const { freezedBalanceSeq: currentSeq } = get()
        if (currentSeq !== freezedBalanceSeq) {
          return
        }
        set({ freezedBalance: undefined, freezedBalanceStatus: false })
      })
    },
    $init: () => {
      set({ isLoading: true, balance: initialUserBalance })
    },
    updateBalance: (balances, partial = false) => {
      let isLoading = get().isLoading
      set(() => ({
        raw: balances as any
      }));

      if (localStorage.getItem(LocalStorageKey.AccountStorage)) {
        set((state) => ({
          isLoading: false,
          balance: {
            ...initialUserBalance,
            ...(partial ? state.balance : {}),
            ...balances,
          },
        }))
      }

      if (isLoading) {
        const denomBalance = Object.values(useBalanceStore.getState().balance)
          .filter((x) => x.amount.humanNumber > 0.01)
          .sort((a, b) => {
            // USDC has highest priority
            if (a.amount.denom == USDC_NEW_DENOM) {
              return -1
            }
            if (b.amount.denom == USDC_NEW_DENOM) {
              return 1
            }
            // SHU has lowest priority
            if (a.amount.denom == SHU_DENOM) {
              return 1
            }
            if (b.amount.denom == SHU_DENOM) {
              return -1
            }
            return 0
          })
          .at(0)?.amount.denom

        // Load stored denom from localStorage
        const storedDenom = localStorage.getItem('ui-denom') && JSON.parse(localStorage.getItem(LocalStorageKey.Denom)!)

        // Auto-migrate old USDC_DENOM to new USDC_NEW_DENOM
        let finalDenom = storedDenom
        if (storedDenom === USDC_DENOM) {
          finalDenom = USDC_NEW_DENOM
          // Save the migrated denom back to localStorage
          localStorage.setItem(LocalStorageKey.Denom, JSON.stringify(USDC_NEW_DENOM))
        }

        useBalanceStore.setState({
          denom: finalDenom || denomBalance || USDC_NEW_DENOM,
        })
      }

      if (useBalanceStore.getState().freezedBalance) {
        for (const [denom, value] of Object.entries(balances)) {
          if (value.height > (useBalanceStore.getState().freezedBalanceHeight || 0)) {
            continue
          }

          set((state) => ({
            freezedBalance: {
              ...state.freezedBalance,
              [denom!]: {
                ...(state.freezedBalance![denom!] || initialUserBalance[denom!]),
                amount: balances[denom!].amount,
              },
            },
          }))
        }
      }

      set((state) => {
        const newBalance = Object.fromEntries(
          Object.entries(state.balance).map(([denom, value]) => [
            denom,
            {
              ...value,
              full_balance: new TokenMoney(value.amount.chainValue.plus(value.game_balance.chainValue), denom),
            },
          ])
        )

        return {
          balance: newBalance,
        }
      })
    },
    clearBalance: () => {
      set({ balance: initialUserBalance })
    },
  }))
)

export const UserBalanceProvider: React.FC<React.PropsWithChildren> = () => {
  const $init = useBalanceStore((state) => state.$init)

  const { account } = useSession()

  useEffect(() => {
    $init()
  }, [account])

  return null
}
